<template>
  <div :class="$style.Wrapper">
    <div
      v-show="props.showSwiperIcon && showLeft"
      :class="[$style.Icon, $style.LeftIcon]"
      @click="handleScrollLeft"
    >
      <LeftOutlined />
    </div>
    <div
      ref="content"
      :style="{
        justifyContent: justifyContent === 'center' && !showLeft && !showRight ? 'center' : null,
      }"
      :class="$style.Content"
      @scroll="handleScroll"
    >
      <div
        :style="{
          marginRight: index === list.length - 1 ? 0 : gap + 'px',
        }"
        :class="$style.Item"
        v-for="(item, index) in list"
        :key="props.listKey ? item[props.listKey] : index"
      >
        <slot name="item" :item="(item as any)" :index="index"></slot>
      </div>
    </div>
    <div
      v-show="props.showSwiperIcon && showRight"
      :class="[$style.Icon, $style.RightIcon]"
      @click="handleScrollRight"
    >
      <RightOutlined />
    </div>
  </div>
</template>

<script lang="ts" setup>
  /**
   * 滑动组件，数据发生变化或父节点、子节点尺寸发生变化会自动计算布局，目前要求子元素宽度都相等
   */
  import { ref, computed, onMounted, watch, nextTick } from 'vue';
  import { useResizeObserver } from '@vueuse/core';
  import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';

  import { throttle } from 'lodash-es';

  const props = defineProps({
    list: {
      type: Array,
      default: () => [],
    },

    // 元素间距
    gap: {
      type: Number,
      default: 10,
    },

    listKey: {
      type: String,
      default: 'id',
      required: false,
    },

    // 当子元素无法滚动时的样式，默认居中
    justifyContent: {
      type: String,
      default: 'center',
    },

    // 将匹配到当前索引所对应的元素滚动到最左侧
    activeIndex: {
      type: Number,
      default: 0,
    },

    // 是否展示左右切换按钮
    showSwiperIcon: {
      type: Boolean,
      default: true,
    },
  });

  const content = ref(null);
  const scrollLeft = ref(0);
  const contentWidth = ref(0);
  const childrenWidth = ref(0);

  // 总宽度
  const totalWidth = computed(() => childrenWidthWithGap.value * props.list.length - props.gap);

  // 单个子元素宽度，包含gap
  const childrenWidthWithGap = computed(() => childrenWidth.value + props.gap);

  // 点击icon滑动一次最大距离
  const stepWidth = computed(
    () => ~~(contentWidth.value / childrenWidthWithGap.value) * childrenWidthWithGap.value
  );

  const showLeft = computed(() => contentWidth.value && scrollLeft.value > 0);

  const showRight = computed(
    () => contentWidth.value && scrollLeft.value + contentWidth.value < totalWidth.value
  );

  function handleScroll(e) {
    scrollLeft.value = Math.ceil(e.target.scrollLeft);
  }

  // 滚动到ActiveIndex对应的位置
  function handleScrollActiveIndexItem() {
    setTimeout(() => {
      smoothScroll({ targetStep: props.activeIndex * childrenWidthWithGap.value, isAdd: false });
    }, 200);
  }

  function smoothScroll({ targetStep, isAdd = true, behavior = 'smooth' }) {
    nextTick(() => {
      const left = isAdd ? scrollLeft.value + targetStep : targetStep;
      content.value?.scrollTo({
        left,
        behavior,
      });
    });
  }

  function handleScrollLeft() {
    const step = scrollLeft.value % childrenWidthWithGap.value;
    const targetStep = -(stepWidth.value + (step > 0 ? step - childrenWidthWithGap.value : step));
    smoothScroll({ targetStep });
  }

  function handleScrollRight() {
    const step = scrollLeft.value % childrenWidthWithGap.value;
    const targetStep = stepWidth.value - (step > 0 ? step - childrenWidthWithGap.value : step);
    smoothScroll({ targetStep });
  }

  function update() {
    if (!content.value) return;

    // 父容器滚动距离
    scrollLeft.value = content.value.scrollLeft;

    // 单个子元素宽度
    childrenWidth.value = content.value.children?.[0]?.offsetWidth;

    // 父容器宽度
    contentWidth.value = Math.min(totalWidth.value, content.value.offsetWidth);
  }

  const updateThrottle = throttle(update, 200);

  watch(
    () => props.list,
    () => {
      nextTick(() => {
        update();
      });
    },
    {
      deep: true,
    }
  );

  watch(
    () => props.activeIndex,
    () => {
      handleScrollActiveIndexItem();
    }
  );

  onMounted(() => {
    update();

    handleScrollActiveIndexItem();

    // 监听父元素发生变化
    useResizeObserver(content.value, () => {
      updateThrottle();
    });

    // 监听子元素发生变化
    if (content.value.children[0]) {
      useResizeObserver(content.value.children[0], () => {
        updateThrottle();
      });
    }
  });
</script>

<style lang="less" module>
  .Wrapper {
    width: 100%;
    position: relative;
  }

  .Content {
    display: flex;
    width: 100%;
    overflow: auto hidden;
    scroll-snap-type: x proximity;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .Item {
    flex-shrink: 0;
    flex-grow: 0;
  }

  .Icon {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 9;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 25px;
    height: 30px;
    color: #fff;
    background: rgba(100, 100, 100, 0.4);
    cursor: pointer;
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 0 0 1px #ea7e00;
      color: #ea7e00;
    }
  }

  .LeftIcon {
    left: 0;
    border-radius: 0 100% 100% 0;
  }

  .RightIcon {
    right: 0;
    border-radius: 100% 0 0 100%;
  }
</style>
